import { useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { setAIPromptEnabled } from '../../../services/aiPromptsApi';
import { Switch, TableBodyCell } from '../../Shared';

const STATUS_CLASS_NAME =
  'inline-flex items-center h-8 px-3 rounded-lg text-[14px]/[20px] font-medium text-base-1000';

export const FEATURE_CELL_WIDTH = '210px';

const getStatus = ({ hasPrompt, isEnabled }) => {
  if (!hasPrompt) {
    return {
      label: 'Prompt not added',
      className: 'bg-base-400/30 text-base-800',
    };
  }
  if (isEnabled) return { label: 'On', className: 'bg-success-500/15' };
  return { label: 'Off', className: 'bg-brand-300/15' };
};

export const FeatureCell = ({
  client,
  feature,
  withBorderBottom,
  onOpenPrompt,
  onChangeStatus,
}) => {
  const status = client[feature.key];
  const { label, className } = getStatus(status);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onToggle = async () => {
    if (!status.hasPrompt) {
      onOpenPrompt(client, feature, true);
      return;
    }

    const isEnabled = !status.isEnabled;
    setIsSubmitting(true);

    try {
      const nextStatus = await setAIPromptEnabled(
        client.tenantId,
        feature.type,
        isEnabled,
      );
      onChangeStatus(client.tenantId, feature.key, nextStatus);
      toast.success(
        `${feature.name} is ${isEnabled ? 'on' : 'off'} for ${client.tenantName}.`,
        { position: 'top-right', autoClose: 3000 },
      );
    } catch {
      toast.error(
        `Failed to turn ${isEnabled ? 'on' : 'off'} ${feature.name}!`,
        {
          position: 'top-right',
          autoClose: 3000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TableBodyCell
      withBorderBottom={withBorderBottom}
      width={FEATURE_CELL_WIDTH}
    >
      <div className="flex items-center gap-2">
        <Switch
          size="small"
          checked={status.isEnabled}
          disabled={isSubmitting}
          title={status.hasPrompt ? undefined : 'Add the prompt to turn it on'}
          inputProps={{
            'aria-label': `${feature.name} for ${client.tenantName}`,
          }}
          onChange={onToggle}
          className="[&_.MuiSwitch-switchBase:not(.Mui-checked)+.MuiSwitch-track]:!bg-base-400 [&_.MuiSwitch-switchBase:not(.Mui-checked)+.MuiSwitch-track]:!opacity-100"
        />
        <span className={twMerge(STATUS_CLASS_NAME, className)}>{label}</span>
      </div>
    </TableBodyCell>
  );
};
